import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { PROVINCES, TRADE_CATEGORIES, URGENCIES } from "@/lib/api";
import JobCard from "@/components/JobCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function JobsBoardPage() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const filters = useMemo(
    () => ({
      province: params.get("province") || "",
      city: params.get("city") || "",
      category: params.get("category") || "",
      urgency: params.get("urgency") || "",
    }),
    [params],
  );

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (filters.province) q.province = filters.province;
    if (filters.city) q.city = filters.city;
    if (filters.category) q.category = filters.category;
    if (filters.urgency) q.urgency = filters.urgency;
    api
      .get("/jobs", { params: q })
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  }, [filters.province, filters.city, filters.category, filters.urgency]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet>
        <title>Open Home-Service Jobs in Canada | Fixus</title>
        <meta name="description" content={`Browse ${list.length} open home-service jobs across Canada. Plumbing, electrical, painting, roofing and more — get hired today.`} />
      </Helmet>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
          Job Board
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">
          Open jobs across Canada
        </h1>
        <p className="text-slate-600 mt-2">{list.length} job{list.length === 1 ? "" : "s"} available</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Province</Label>
            <Select value={filters.province || "all"} onValueChange={(v) => update("province", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="jobs-filter-province" className="mt-2">
                <SelectValue placeholder="All provinces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">City</Label>
            <Input
              data-testid="jobs-filter-city"
              value={filters.city}
              onChange={(e) => update("city", e.target.value)}
              placeholder="e.g. Toronto"
              className="mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Trade</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => update("category", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="jobs-filter-category" className="mt-2">
                <SelectValue placeholder="All trades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All trades</SelectItem>
                {TRADE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Urgency</Label>
            <Select value={filters.urgency || "all"} onValueChange={(v) => update("urgency", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="jobs-filter-urgency" className="mt-2">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {URGENCIES.map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="jobs-filter-clear" variant="outline" onClick={() => setParams({})} className="w-full">
            Clear filters
          </Button>
        </aside>

        <div>
          {renderJobsList({ loading, list })}
        </div>
      </div>
    </div>
  );
}

function renderJobsList({ loading, list }) {
  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (list.length === 0) {
    return (
      <div className="text-slate-500" data-testid="empty-jobs">
        No open jobs match these filters.
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5 tf-stagger">
      {list.map((job) => (
        <JobCard key={job.id} job={job} />
      ))}
    </div>
  );
}
